///
/// EarthBrowser Admin Dashboard - Email Template Preview
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Eye, Mail, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'https://earthbrowser.com'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AUTO_FILLED_VARS = new Set(['email', 'appName', 'appUrl', 'unsubscribeUrl'])

interface EmailTemplate {
  id: string
  name: string
  description: string
  requiredVariables: string[]
}

interface PreviewResponse {
  subject: string
  html: string
  text: string
  missingVariables?: string[]
}

interface SendResponse {
  success: boolean
  message: string
  sent?: number
  failed?: number
  total?: number
  errors?: { email: string; error: string }[]
}

function parseEmails(raw: string): string[] {
  return raw.split(/[,\n]/).map(e => e.trim()).filter(Boolean)
}

export function EmailTemplatePreview() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [recipientText, setRecipientText] = useState('')
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recipientError, setRecipientError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [showSendDialog, setShowSendDialog] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<string | null>(null)

  const template = templates.find(t => t.id === selectedTemplate)

  const editableVars = useMemo(() =>
    template?.requiredVariables.filter(v => !AUTO_FILLED_VARS.has(v)) ?? [],
    [template]
  )

  const recipients = useMemo(() => parseEmails(recipientText), [recipientText])

  useEffect(() => {
    void loadTemplates()
  }, [])

  useEffect(() => {
    if (selectedTemplate && template) {
      const sampleData: Record<string, string> = {
        email: 'user@example.com',
        name: 'John Doe',
        appName: 'EarthBrowser',
        appUrl: 'https://earthbrowser.com',
        unsubscribeUrl: 'https://earthbrowser.com/unsubscribe?token=sample',
        verificationUrl: 'https://earthbrowser.com/verify?token=sample',
        resetUrl: 'https://earthbrowser.com/reset?token=sample',
        expirationMinutes: '60',
      }

      const initialVars: Record<string, string> = {}
      template.requiredVariables.forEach(varName => {
        initialVars[varName] = sampleData[varName] || `Sample ${varName}`
      })
      setVariables(initialVars)
      setRecipientText('')
      setSendResult(null)
    }
  }, [selectedTemplate, templates])

  useEffect(() => {
    if (selectedTemplate && Object.keys(variables).length > 0) {
      const timer = setTimeout(() => {
        void loadPreview()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [variables, selectedTemplate])

  async function loadTemplates() {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates`, {
        credentials: 'include',
      })
      if (!response.ok) throw new Error('Failed to load templates')
      const data = await response.json() as { templates: EmailTemplate[] }
      setTemplates(data.templates)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    }
  }

  async function loadPreview() {
    if (!selectedTemplate) return
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${selectedTemplate}/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables }),
      })
      if (!response.ok) throw new Error('Failed to load preview')
      const data = await response.json() as PreviewResponse
      setPreview(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview')
    } finally {
      setLoading(false)
    }
  }

  function validateForSend(): boolean {
    const errors: Record<string, string> = {}

    for (const varName of editableVars) {
      if (!variables[varName]?.trim()) {
        errors[varName] = `${varName} is required`
      }
    }
    setValidationErrors(errors)

    if (recipients.length === 0) {
      setRecipientError('At least one email address is required')
      return false
    }

    const invalid = recipients.filter(e => !emailRegex.test(e))
    if (invalid.length > 0) {
      setRecipientError(`Invalid addresses: ${invalid.join(', ')}`)
      return false
    }

    setRecipientError(null)
    return Object.keys(errors).length === 0
  }

  async function sendEmails() {
    setSending(true)
    setError(null)
    setSendResult(null)

    try {
      const response = await fetch(`${API_URL}/api/admin/test-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipients,
          templateId: selectedTemplate,
          variables,
        }),
      })

      const contentType = response.headers.get('content-type')
      if (!response.ok) {
        if (contentType?.includes('application/json')) {
          const errorData = await response.json() as { message?: string }
          throw new Error(errorData.message || 'Failed to send email')
        }
        throw new Error(`Failed to send email (${response.status})`)
      }

      const data = await response.json() as SendResponse
      if (recipients.length === 1) {
        setSendResult(`Email sent to ${recipients[0]}`)
      } else {
        const parts = [`Sent ${data.sent} of ${data.total}`]
        if (data.failed && data.failed > 0) {
          parts.push(`${data.failed} failed`)
        }
        setSendResult(parts.join(' — '))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email')
    } finally {
      setSending(false)
      setShowSendDialog(false)
    }
  }

  function handleSendClick() {
    if (!validateForSend()) return
    setShowSendDialog(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-cyan-600" />
        <h3 className="text-lg font-bold text-foreground">Email Template Preview</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Template</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="w-full rounded-xl focus:ring-2 focus:ring-blue-500">
                <SelectValue placeholder="Choose template..." />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="rounded-lg">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {template && (
              <p className="text-xs text-muted-foreground">{template.description}</p>
            )}
          </div>

          {template && editableVars.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Variables</Label>
              <div className="space-y-2">
                {editableVars.map((varName) => (
                  <div key={varName} className="space-y-1">
                    <Label htmlFor={varName} className="text-xs text-muted-foreground">{varName}</Label>
                    <Input
                      id={varName}
                      value={variables[varName] || ''}
                      onChange={(e) => {
                        setVariables({ ...variables, [varName]: e.target.value })
                        if (validationErrors[varName]) {
                          setValidationErrors({ ...validationErrors, [varName]: '' })
                        }
                      }}
                      placeholder={varName}
                      className={`h-8 text-sm rounded-xl transition-all duration-300 focus-visible:ring-2 ${validationErrors[varName] ? 'border-red-500 focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`}
                    />
                    {validationErrors[varName] && (
                      <p className="text-xs text-red-600">{validationErrors[varName]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {template && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Recipients</Label>
              <Textarea
                value={recipientText}
                onChange={(e) => {
                  setRecipientText(e.target.value)
                  setRecipientError(null)
                }}
                placeholder="Comma-separated email addresses"
                rows={3}
                className={`text-sm rounded-xl transition-all duration-300 focus-visible:ring-2 ${recipientError ? 'border-red-500 focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`}
              />
              {recipients.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
                </p>
              )}
              {recipientError && (
                <p className="text-xs text-red-600">{recipientError}</p>
              )}
            </div>
          )}

          {template && recipients.length > 0 && (
            <Button
              onClick={handleSendClick}
              disabled={sending}
              variant="destructive"
              className="w-full rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending
                ? 'Sending...'
                : recipients.length === 1
                  ? 'Send Email'
                  : `Send to ${recipients.length} Recipients`}
            </Button>
          )}

          {sendResult && (
            <Alert className="rounded-xl bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 border-emerald-500/20">
              <AlertDescription className="text-xs">{sendResult}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="rounded-xl">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}

          {loading && (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Eye className="h-3 w-3 animate-pulse" />
              Updating preview...
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          {preview ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-muted-foreground">Subject Line</Label>
                <div className="border rounded-xl p-4 bg-muted">
                  <p className="text-sm font-semibold">{preview.subject}</p>
                </div>
                {preview.missingVariables && preview.missingVariables.length > 0 && (
                  <Alert className="rounded-xl bg-amber-500/10 border-amber-500/20">
                    <AlertDescription className="text-xs">
                      <strong>Missing variables:</strong> {preview.missingVariables.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Tabs defaultValue="html" className="w-full">
                <TabsList className="grid w-full grid-cols-2 rounded-xl">
                  <TabsTrigger value="html" className="rounded-lg">HTML Preview</TabsTrigger>
                  <TabsTrigger value="text" className="rounded-lg">Plain Text</TabsTrigger>
                </TabsList>
                <TabsContent value="html" className="mt-4">
                  <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                    <iframe
                      srcDoc={preview.html}
                      className="w-full h-[500px]"
                      title="Email HTML Preview"
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                </TabsContent>
                <TabsContent value="text" className="mt-4">
                  <div className="border rounded-xl p-4 bg-muted font-mono text-xs whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                    {preview.text}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="border-2 border-dashed border-cyan-500/30 bg-cyan-500/5 rounded-xl h-[500px] flex items-center justify-center text-muted-foreground">
              <div className="text-center space-y-3">
                <div className="p-3 rounded-full bg-cyan-500/10 inline-block mb-2">
                  <Eye className="h-8 w-8 text-cyan-500" />
                </div>
                <p className="text-sm font-medium">Select a template to preview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {recipients.length === 1 ? 'Send Email?' : `Send to ${recipients.length} Recipients?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {recipients.length === 1
                ? 'You are about to send an email with the following details:'
                : `You are about to send this email to ${recipients.length} recipients:`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-4">
            <div className="bg-muted p-3 rounded-xl text-sm space-y-1">
              <div><strong>Template:</strong> {template?.name}</div>
              <div><strong>Subject:</strong> {preview?.subject}</div>
              {recipients.length === 1 ? (
                <div><strong>To:</strong> {recipients[0]}</div>
              ) : (
                <div><strong>Recipients:</strong> {recipients.length} addresses</div>
              )}
            </div>
            {recipients.length > 1 && (
              <div className="max-h-32 overflow-y-auto bg-muted/50 p-2 rounded-lg text-xs text-muted-foreground font-mono">
                {recipients.join(', ')}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={sendEmails}
              disabled={sending}
              className="rounded-full bg-red-600 hover:bg-red-700 transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-red-500 disabled:hover:scale-100"
            >
              {sending
                ? 'Sending...'
                : recipients.length === 1
                  ? 'Send Email'
                  : `Send to ${recipients.length} Recipients`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

